import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Stethoscope, Target, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

import { SAMPLE_REPORT } from "@/pages/free-screening/sampleReport";
import { toneStyles } from "@/pages/free-screening/lib/helpers";

/**
 * SampleReportDialog — the "Kiddo+ Sample Report" popup used on both
 * ``/free-screening`` (hero + mid-page CTAs) and ``/`` (Landing "Your
 * Report" section, added in the marketing polish batch of 2026-02).
 *
 * Extracted verbatim from the inline dialog in FreeScreening.jsx so
 * multiple entry points can share the same 200-line JSX without duplication.
 *
 * Props:
 *   open           : boolean         — controlled visibility
 *   onOpenChange   : (bool) => void  — controlled open/close callback
 *   dataTestId?    : string          — override for the DialogContent
 *                                      data-testid (default: sample-report-dialog)
 *   onCtaClick?    : () => void      — override for the "Get My Free Screening"
 *                                      button. If omitted, defaults to
 *                                      closing the dialog and navigating to
 *                                      /free-screening#lead-form so the
 *                                      Landing CTA still funnels correctly.
 */
export default function SampleReportDialog({
  open,
  onOpenChange,
  dataTestId = "sample-report-dialog",
  onCtaClick,
}) {
  const navigate = useNavigate();
  const handleCta = () => {
    if (onCtaClick) {
      onCtaClick();
      return;
    }
    onOpenChange?.(false);
    navigate("/free-screening#lead-form");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={dataTestId}
        className="p-0 overflow-hidden border-0 rounded-3xl max-w-3xl w-[calc(100vw-1.25rem)] sm:w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col"
      >
        <div className="bg-gradient-to-br from-[#F0FDFA] to-white px-5 sm:px-8 pt-6 pb-5 border-b border-slate-100">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-[#2F6F6A]">Kiddo+ Sample Report</p>
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider bg-[#14B8A6]/15 text-[#0f766e] rounded-full px-2 py-0.5">
                <CheckCircle2 className="h-3 w-3" /> Verified
              </span>
            </div>
            <DialogTitle className="font-display text-xl sm:text-2xl font-extrabold text-[#1F2933]">
              Developmental Report — {SAMPLE_REPORT.childName}
            </DialogTitle>
            <DialogDescription className="text-[13px] sm:text-sm text-slate-600 mt-1">
              DOB {SAMPLE_REPORT.meta.dob} · Assessment {SAMPLE_REPORT.meta.assessmentDate} · {SAMPLE_REPORT.meta.gender}
            </DialogDescription>
            <div className="mt-3 flex items-center gap-2.5 bg-white rounded-2xl px-3 py-2 border border-slate-100 shadow-sm w-fit">
              <div className="h-9 w-9 rounded-full bg-[#F0FDFA] flex items-center justify-center shrink-0">
                <Stethoscope className="h-4 w-4 text-[#14B8A6]" />
              </div>
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Verified by</p>
                <p className="text-[13px] font-bold text-[#1F2933]">{SAMPLE_REPORT.verifiedBy}</p>
                <p className="text-[11px] text-slate-500">{SAMPLE_REPORT.verifiedCreds}</p>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-8 py-5 overflow-y-auto space-y-7 bg-slate-50/40">
          {/* Profile */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-rose-500" />
                <p className="text-[11px] uppercase tracking-wider font-bold text-[#2F6F6A]">Main Concern</p>
              </div>
              <p className="text-[13px] sm:text-sm text-slate-700 leading-relaxed mt-2">{SAMPLE_REPORT.mainConcern}</p>
            </div>
            <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#14B8A6]" />
                <p className="text-[11px] uppercase tracking-wider font-bold text-[#2F6F6A]">Goals</p>
              </div>
              <p className="text-[13px] sm:text-sm text-slate-700 leading-relaxed mt-2">{SAMPLE_REPORT.goals}</p>
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="font-semibold text-slate-500 uppercase tracking-wider">Next Reassessment</span>
                  <span className="font-extrabold text-[#1F2933] tabular-nums">{SAMPLE_REPORT.reassessmentProgress} / {SAMPLE_REPORT.reassessmentTotal} <span className="text-slate-500 font-semibold">activity days</span></span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(SAMPLE_REPORT.reassessmentProgress / SAMPLE_REPORT.reassessmentTotal) * 100}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-[#14B8A6] to-[#2F6F6A]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Focus areas */}
          <div>
            <h4 className="font-display font-extrabold text-[#1F2933] text-base sm:text-lg">Parent-Selected Focus Areas</h4>
            <div className="mt-3 flex flex-wrap gap-2">
              {SAMPLE_REPORT.focusAreas.map((f) => (
                <span key={f} className="text-[11px] sm:text-[12px] font-semibold bg-white text-[#1F2933] border border-slate-200 rounded-full px-3 py-1 shadow-sm">
                  {f}
                </span>
              ))}
            </div>
          </div>

          {/* Domain scores */}
          <div>
            <h4 className="font-display font-extrabold text-[#1F2933] text-base sm:text-lg">Domain Assessment Summary</h4>
            <div className="mt-4 space-y-3 bg-white rounded-2xl p-4 sm:p-5 border border-slate-100 shadow-sm">
              {SAMPLE_REPORT.domains.map((d, i) => {
                const tone = toneStyles(d.tone);
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-[13px] sm:text-sm mb-1">
                      <span className="font-semibold text-[#1F2933]">{d.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-700 tabular-nums">{d.score}%</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${tone.chip}`}>
                          {d.status}
                        </span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${tone.bar}`} style={{ width: `${d.score}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Targeted guidance — detailed cards */}
          <div>
            <h4 className="font-display font-extrabold text-[#1F2933] text-base sm:text-lg">Targeted Domain Guidance</h4>
            <p className="text-[13px] text-slate-500 mt-1">Each domain includes observation, strategy, practical home tips, and a 4-week milestone.</p>
            <div className="mt-4 space-y-4">
              {SAMPLE_REPORT.guidance.map((g, i) => {
                const tone = toneStyles(g.tone);
                return (
                  <div key={i} className={`rounded-2xl border ${tone.border} bg-white overflow-hidden shadow-sm`}>
                    <div className={`${tone.soft} px-4 sm:px-5 py-3 flex items-center justify-between gap-3 border-b ${tone.border}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-xl bg-white shadow-sm flex items-center justify-center font-display font-extrabold text-[#1F2933] tabular-nums text-[13px] border ${tone.border}`}>
                          {g.score}%
                        </div>
                        <p className="font-display font-extrabold text-[14px] sm:text-[15px] text-[#1F2933]">{g.title}</p>
                      </div>
                      <span className={`hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${tone.chip}`}>
                        {g.tone === "ok" ? "Typical" : g.tone === "warn" ? "Mild–Moderate" : "Significant"}
                      </span>
                    </div>
                    <div className="p-4 sm:p-5 space-y-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Observation</p>
                        <p className="text-[13px] sm:text-sm text-slate-700 mt-1 leading-relaxed">{g.observation}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Therapist Strategy</p>
                        <p className="text-[13px] sm:text-sm text-slate-700 italic mt-1 leading-relaxed">{g.strategy}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Daily Home Activities</p>
                        <ul className="mt-2 space-y-1.5">
                          {g.tips.map((tip, k) => (
                            <li key={k} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                              <CheckCircle2 className={`h-4 w-4 ${tone.text} shrink-0 mt-0.5`} />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex items-start gap-2 rounded-xl bg-[#F8FAFC] border border-slate-100 px-3 py-2.5">
                        <Target className="h-4 w-4 text-[#14B8A6] shrink-0 mt-0.5" />
                        <p className="text-[12px] sm:text-[13px] text-slate-700 leading-snug"><span className="font-bold text-[#1F2933]">4-Week Milestone — </span>{g.milestone}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sub-skill deep dive */}
          <div>
            <h4 className="font-display font-extrabold text-[#1F2933] text-base sm:text-lg">Sub-Skill Deep Dive</h4>
            <p className="text-[13px] text-slate-500 mt-1">Specific strengths and focus areas hidden inside each domain.</p>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {SAMPLE_REPORT.subSkills.map((s, i) => (
                <div key={i} className="relative rounded-2xl bg-white p-5 border border-slate-100 shadow-sm overflow-hidden">
                  <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-[#14B8A6]/10" />
                  <p className="font-display font-extrabold text-[14px] text-[#1F2933] relative">{s.domain}</p>

                  <div className="mt-4 relative">
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="font-bold text-[#0f766e] uppercase tracking-wider">Strength</span>
                      <span className="font-bold text-[#0f766e] tabular-nums">{s.strength.score}%</span>
                    </div>
                    <p className="text-[12.5px] text-slate-700 leading-snug">{s.strength.label}</p>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${s.strength.score}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="h-full bg-[#14B8A6]"
                      />
                    </div>
                  </div>

                  <div className="mt-4 relative">
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="font-bold text-rose-600 uppercase tracking-wider">Focus Area</span>
                      <span className="font-bold text-rose-600 tabular-nums">{s.focus.score}%</span>
                    </div>
                    <p className="text-[12.5px] text-slate-700 leading-snug">{s.focus.label}</p>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${s.focus.score}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="h-full bg-rose-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4 relative rounded-xl bg-[#F8FAFC] border border-slate-100 p-3">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F6F6A]">Therapist Note</p>
                    <p className="text-[12px] text-slate-700 mt-1 leading-relaxed">{s.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-[#2F6F6A] text-white p-5 sm:p-6 text-center">
            <p className="font-display font-extrabold text-lg sm:text-xl">Get your child&apos;s personalized report</p>
            <p className="text-[13px] text-white/85 mt-1">Free screening • Delivered on WhatsApp by Dr. Dipak</p>
            <button
              onClick={handleCta}
              data-testid="sample-report-cta"
              className="mt-4 inline-flex items-center gap-2 bg-[#F4C430] hover:bg-[#fbd34c] text-[#1F2933] rounded-full px-6 py-3 font-extrabold text-sm sm:text-base shadow"
            >
              Get My Free Screening <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
